import { Alert, Card, Spin, Table, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import React, { useEffect, useMemo, useState } from 'react'
import { fetchCrowsNestTimeToConversion } from 'src/utils/crowsNestApi'
import { formatFunnelScopeLabel } from 'src/utils/crowsNestFunnel'
import { mapTimeToConversion } from 'src/utils/crowsNestTimeToConversion'
import { CROWS_NEST_TOOLTIPS } from 'src/utils/crowsNestTooltips'
import { crowsNestErrorMessage } from 'src/utils/crowsNestErrors'
import styled from 'styled-components/macro'
import CrowsNestTipLabel from './CrowsNestTipLabel'

const CONVERSION_PLAN = 'Free'

const compareMedian = (a, b, key) => {
  const left = a[key]?.medianDays
  const right = b[key]?.medianDays
  const leftVal = left == null || Number.isNaN(Number(left)) ? -1 : Number(left)
  const rightVal =
    right == null || Number.isNaN(Number(right)) ? -1 : Number(right)
  return leftVal - rightVal
}

/**
 * Median days to first success and to paywall — empty stays quiet ("—").
 */
const CrowsNestTimeToConversion = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [summary, setSummary] = useState(null)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await fetchCrowsNestTimeToConversion({
          plan: CONVERSION_PLAN,
        })
        if (cancelled) return
        setSummary(mapTimeToConversion(response))
      } catch (err) {
        if (!cancelled) {
          setError(
            crowsNestErrorMessage(
              err,
              "Couldn't load time to conversion. Try again in a moment.",
            ),
          )
          setSummary(null)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  const columns = useMemo(
    () => [
      {
        title: 'Feature',
        dataIndex: 'featureName',
        key: 'feature',
        sorter: (a, b) =>
          String(a.featureName || '').localeCompare(
            String(b.featureName || ''),
          ),
      },
      {
        title: (
          <CrowsNestTipLabel
            label='Time to first success'
            tip={CROWS_NEST_TOOLTIPS.timeToFirstSuccess}
          />
        ),
        key: 'timeToFirstSuccess',
        sorter: (a, b) => compareMedian(a, b, 'timeToFirstSuccess'),
        render: (_value, row) => (
          <DurationCell>
            <span>{row.timeToFirstSuccess.medianLabel}</span>
            <Sample>{row.timeToFirstSuccess.sampleLabel}</Sample>
          </DurationCell>
        ),
      },
      {
        title: (
          <CrowsNestTipLabel
            label='Time to paywall'
            tip={CROWS_NEST_TOOLTIPS.timeToPaywall}
          />
        ),
        key: 'timeToPaywall',
        sorter: (a, b) => compareMedian(a, b, 'timeToPaywall'),
        render: (_value, row) => (
          <DurationCell>
            <span>{row.timeToPaywall.medianLabel}</span>
            <Sample>{row.timeToPaywall.sampleLabel}</Sample>
          </DurationCell>
        ),
      },
    ],
    [],
  )

  const totals = summary?.totals
  const scopeLabel = formatFunnelScopeLabel(summary?.range, CONVERSION_PLAN)

  return (
    <StatsCard
      data-testid='crows-nest-time-to-conversion'
      title={
        <TitleBlock>
          <TitleRow>
            <span>Time to conversion</span>
            <Tooltip title={CROWS_NEST_TOOLTIPS.timeToConversionSection}>
              <InfoCircleOutlined />
            </Tooltip>
          </TitleRow>
          {summary ? (
            <ScopeLabel data-testid='conversion-scope-label'>
              {scopeLabel}
            </ScopeLabel>
          ) : null}
        </TitleBlock>
      }
    >
      {error && !summary ? (
        <Alert type='error' showIcon message={error} />
      ) : null}

      {loading && !summary ? (
        <LoadingWrap>
          <Spin />
        </LoadingWrap>
      ) : null}

      {totals ? (
        <>
          <TotalsRow>
            <TotalStat>
              <TotalLabel>
                <CrowsNestTipLabel
                  label='Time to first success'
                  tip={CROWS_NEST_TOOLTIPS.timeToFirstSuccess}
                />
              </TotalLabel>
              <TotalValue data-testid='conversion-total-first-success'>
                {totals.timeToFirstSuccess.medianLabel}
              </TotalValue>
              <TotalSample data-testid='conversion-sample-first-success'>
                {totals.timeToFirstSuccess.sampleLabel}
              </TotalSample>
            </TotalStat>
            <TotalStat>
              <TotalLabel>
                <CrowsNestTipLabel
                  label='Time to paywall'
                  tip={CROWS_NEST_TOOLTIPS.timeToPaywall}
                />
              </TotalLabel>
              <TotalValue data-testid='conversion-total-paywall'>
                {totals.timeToPaywall.medianLabel}
              </TotalValue>
              <TotalSample data-testid='conversion-sample-paywall'>
                {totals.timeToPaywall.sampleLabel}
              </TotalSample>
            </TotalStat>
          </TotalsRow>

          <Table
            rowKey='key'
            columns={columns}
            dataSource={summary.byFeature}
            pagination={false}
            size='middle'
            locale={{ emptyText: 'No conversion timings yet for Free venues.' }}
            scroll={{ x: true }}
          />
        </>
      ) : null}
    </StatsCard>
  )
}

const StatsCard = styled(Card)`
  .ant-card-head-title {
    font-weight: 600;
  }

  .ant-table-thead > tr > th {
    white-space: nowrap;
  }
`

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const TitleRow = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 8px;

  .anticon {
    color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
    font-size: 14px;
  }
`

const ScopeLabel = styled.span`
  color: ${({ theme }) => theme.colours.oliveGrey || '#666'};
  font-size: 12px;
  font-weight: 500;
`

const LoadingWrap = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 120px;
`

const TotalsRow = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.md || '16px'};
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  margin-bottom: ${({ theme }) => theme.spacing.md || '16px'};
`

const TotalStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const TotalLabel = styled.div`
  color: ${({ theme }) => theme.colours.oliveGrey || '#666'};
  font-size: 13px;
  font-weight: 500;
`

const TotalValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  line-height: 1.1;
`

const TotalSample = styled.div`
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
  font-size: 12px;
`

const DurationCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const Sample = styled.span`
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
  font-size: 11px;
`

export default CrowsNestTimeToConversion
